import React, { useEffect, useMemo } from "react";
import { CacheProvider } from "@emotion/react";
import { ThemeProvider } from "@mui/material";
import { useTranslation } from "react-i18next";
import { createAppTheme } from "../theme";
import { createLtrCache, createRtlCache } from "../utils/createEmotionCache";
import { isRtlLanguage, normalizeLanguage } from "../i18n/language";

type AppThemeProviderProps = {
  children: React.ReactNode;
};

const AppThemeProvider = ({ children }: AppThemeProviderProps) => {
  const { i18n } = useTranslation();
  const language =
    normalizeLanguage(i18n.resolvedLanguage || i18n.language) ?? "ru";
  const direction: "ltr" | "rtl" = isRtlLanguage(language) ? "rtl" : "ltr";

  const cache = useMemo(
    () => (direction === "rtl" ? createRtlCache() : createLtrCache()),
    [direction],
  );

  const theme = useMemo(
    () => createAppTheme(direction, language),
    [direction, language],
  );

  useEffect(() => {
    document.documentElement.setAttribute("dir", direction);
    document.documentElement.setAttribute("lang", language);
  }, [direction, language]);

  return (
    <CacheProvider value={cache}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </CacheProvider>
  );
};

export default AppThemeProvider;
